import { DataCircle } from './DataCircle.tsx';
import {
  Box,
  Column,
  Columns,
  Stack,
  Text,
  useColor,
} from 'braid-design-system';
import { useRef } from 'react';
import {
  arrow,
  autoPlacement,
  autoUpdate,
  FloatingArrow,
  offset,
  useFloating,
} from '@floating-ui/react';

export type TooltipData = {
  title: string;
  points: Array<{
    text: string;
    color: string;
  }>;
};

// science
const ARROW_SIZE = 6;
const ARROW_OFFSET = 3;

export const useGraphTooltipFloating = () => {
  const arrowRef = useRef(null);

  return {
    ...useFloating({
      whileElementsMounted: autoUpdate,
      strategy: 'fixed',
      middleware: [
        autoPlacement({ allowedPlacements: ['left', 'right'] }),
        arrow({ element: arrowRef, padding: 0 }),
        offset({
          mainAxis: ARROW_SIZE + ARROW_OFFSET,
          crossAxis: ARROW_SIZE * 2.5,
        }),
      ],
    }),
    arrowRef,
  };
};

export const GraphTooltip = ({
  tooltipData,
  floating: { refs, context, floatingStyles, arrowRef },
}: {
  tooltipData: TooltipData | null;
  floating: ReturnType<typeof useGraphTooltipFloating>;
}) => {
  const color = useColor();

  if (!tooltipData) {
    return null;
  }

  return (
    <Box
      ref={refs.setFloating}
      background="neutralActive"
      style={floatingStyles}
      borderRadius="standard"
      padding="small"
      maxWidth="xsmall"
      zIndex="dropdown"
      userSelect="none"
      pointerEvents="none"
    >
      <Stack space="medium">
        <Text size="small">{tooltipData.title}</Text>

        <Box paddingLeft="xxsmall">
          <Stack space="xsmall">
            {tooltipData.points.map((point, i) => (
              <Columns space="xxsmall" key={i}>
                <Column width="content">
                  <DataCircle color={point.color} />
                </Column>
                <Column>
                  <Text size="small">{point.text}</Text>
                </Column>
              </Columns>
            ))}
          </Stack>
        </Box>
      </Stack>

      <FloatingArrow
        ref={arrowRef}
        context={context}
        width={ARROW_SIZE * 2}
        height={ARROW_SIZE}
        fill={color.background.neutralActive}
      />
    </Box>
  );
};
